import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftRight, Plus, X, TrendingUp, AlertCircle, Globe, ChevronDown } from 'lucide-react';
import { api } from '../lib/api';
import { useAuthStore } from '../store/auth.store';
import { formatDate } from '../lib/formatters';

const inputCls = 'w-full bg-[#0b1120] border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white font-mono focus:outline-none focus:border-cyan-500/40 transition-colors placeholder-slate-700';

interface RateHistoryEntry {
  id: string;
  ukcPerUnit: string;
  effectiveAt: string;
  deprecatedAt: string | null;
  setByUserId: string | null;
}

// Inline SVG sparkline for the rate history trend
function RateSparkline({ history }: { history: RateHistoryEntry[] }) {
  const sorted = history.slice().sort((a, b) => new Date(a.effectiveAt).getTime() - new Date(b.effectiveAt).getTime());
  if (sorted.length < 2) return null;
  const W = 120; const H = 28;
  const vals  = sorted.map((h) => parseFloat(h.ukcPerUnit));
  const minV  = Math.min(...vals);
  const maxV  = Math.max(...vals);
  const range = maxV - minV || 0.0001;
  const pts = sorted.map((h, i) => {
    const x = (i / (sorted.length - 1)) * W;
    const y = H - ((parseFloat(h.ukcPerUnit) - minV) / range) * (H - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg width={W} height={H} className="mt-2 opacity-70">
      <polyline points={pts} fill="none" stroke="#22d3ee" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function ExchangeRateCard({ worldId, worldName, currencySymbol, index }: { worldId: string; worldName: string; currencySymbol: string; index: number }) {
  const [showHistory, setShowHistory] = useState(false);

  const { data: rate, isLoading } = useQuery({
    queryKey: ['exchange-rates', worldId],
    queryFn: () => api.get(`/central-bank/exchange-rates/${worldId}`).then((r) => r.data).catch(() => null),
  });

  const { data: history = [] } = useQuery<RateHistoryEntry[]>({
    queryKey: ['exchange-rate-history', worldId],
    queryFn: () => api.get(`/central-bank/exchange-rates/${worldId}/history`).then((r) => r.data),
    enabled: showHistory,
    staleTime: 60_000,
  });

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.06, duration: 0.3 }}
      className="glass-card relative overflow-hidden"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent" />
      <div className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-lg bg-cyan-500/10 border border-cyan-500/15 flex items-center justify-center">
            <Globe className="w-3.5 h-3.5 text-cyan-400/70" />
          </div>
          <div>
            <p className="text-sm font-semibold text-white">{worldName}</p>
            <p className="text-[10px] font-mono text-slate-600">{currencySymbol}</p>
          </div>
        </div>
        {isLoading ? (
          <div className="space-y-2">
            <div className="shimmer h-7 w-32 rounded-lg" />
            <div className="shimmer h-3 w-24 rounded" />
          </div>
        ) : rate ? (
          <>
            <p className="text-2xl font-bold text-cyan-300 font-mono">{parseFloat(rate.ukcPerUnit).toFixed(6)}</p>
            <p className="text-xs text-slate-600 mt-1">1 {currencySymbol} = {parseFloat(rate.ukcPerUnit).toFixed(8)} UKC</p>
            <p className="text-xs text-slate-700 mt-2">Since {formatDate(rate.effectiveAt)}</p>
          </>
        ) : (
          <div className="flex items-center gap-1.5 text-amber-400 text-xs">
            <AlertCircle className="w-3.5 h-3.5" />
            No rate set
          </div>
        )}

        {/* History toggle */}
        <button
          onClick={() => setShowHistory((v) => !v)}
          className="mt-3 flex items-center gap-1 text-[10px] text-slate-500 hover:text-slate-300 transition-colors"
        >
          <ChevronDown className={`w-3 h-3 transition-transform ${showHistory ? 'rotate-180' : ''}`} />
          {showHistory ? 'Hide history' : 'Show history'}
        </button>
      </div>

      <AnimatePresence initial={false}>
        {showHistory && (
          <motion.div
            key="hist"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden border-t border-white/[0.04]"
          >
            <div className="px-5 pb-4 pt-3">
              {history.length === 0 ? (
                <p className="text-xs text-slate-600 text-center py-2">No history available.</p>
              ) : (
                <>
                  <RateSparkline history={history} />
                  <div className="mt-3 space-y-1 max-h-48 overflow-y-auto">
                    {history.map((h) => (
                      <div key={h.id} className="flex justify-between text-[10px]">
                        <span className="text-slate-500">{formatDate(h.effectiveAt)}</span>
                        <span className="font-mono text-cyan-400">{parseFloat(h.ukcPerUnit).toFixed(8)}</span>
                        <span className="text-slate-600">{h.deprecatedAt ? `→ ${formatDate(h.deprecatedAt)}` : 'current'}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export function ExchangeRatesPage() {
  const qc = useQueryClient();
  const role = useAuthStore((s) => s.role);
  const [worldId, setWorldId] = useState('');
  const [rate, setRate] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState(false);

  const { data: worlds = [] } = useQuery({
    queryKey: ['worlds'],
    queryFn: () => api.get('/worlds').then((r) => r.data),
  });

  const selectedWorld = worlds.find((w: Record<string, string>) => w['id'] === worldId);

  const setRateMutation = useMutation({
    mutationFn: (data: object) => api.post('/central-bank/exchange-rates', data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['exchange-rates'] }); setShowForm(false); setWorldId(''); setRate(''); setPendingConfirm(false); },
  });

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Exchange Rates</h2>
          <p className="text-sm text-slate-600 mt-0.5">Central Bank managed rates to UKC</p>
        </div>
        {role === 'CENTRAL_BANK_ADMIN' && (
          <button
            onClick={() => { setShowForm(!showForm); setPendingConfirm(false); }}
            className="flex items-center gap-2 btn-primary text-xs font-semibold px-4 py-2 rounded-xl"
          >
            {showForm ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
            {showForm ? 'Cancel' : 'Set Rate'}
          </button>
        )}
      </div>

      {/* CB-only banner */}
      <div className="flex items-start gap-3 glass-card p-4" style={{ borderColor: 'rgba(245,158,11,0.25)' }}>
        <div className="w-8 h-8 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
          <TrendingUp className="w-4 h-4 text-amber-400" />
        </div>
        <div>
          <p className="text-sm font-semibold text-amber-300">Central Bank Exclusive</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Rates are set exclusively by the Central Bank. All rates express: 1 World Currency = N UKC.
          </p>
        </div>
      </div>

      {/* Set rate form — CENTRAL_BANK_ADMIN only */}
      {showForm && role === 'CENTRAL_BANK_ADMIN' && (
        <motion.form
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="glass-card p-6 space-y-5"
          style={{ borderColor: 'rgba(0,212,255,0.2)' }}
          onSubmit={(e) => {
            e.preventDefault();
            if (!pendingConfirm) { setPendingConfirm(true); return; }
            setRateMutation.mutate({ worldId, ukcPerUnit: rate });
          }}
        >
          <div className="flex items-center gap-2 mb-1">
            <ArrowLeftRight className="w-4 h-4 text-cyan-400" />
            <h3 className="text-sm font-semibold text-white">Set Exchange Rate</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-2">World <span className="text-red-400 ml-0.5">*</span></label>
              <select
                value={worldId}
                onChange={(e) => setWorldId(e.target.value)}
                required
                className="w-full bg-[#0b1120] border border-white/8 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-500/40 transition-colors"
              >
                <option value="">Select world...</option>
                {worlds.map((w: Record<string, string>) => (
                  <option key={w['id']} value={w['id']}>{w['name']} ({w['currencySymbol']})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-2">
                Rate (1 {selectedWorld?.currencySymbol ?? 'WLD'} = N UKC) <span className="text-red-400 ml-0.5">*</span>
              </label>
              <input
                type="number"
                step="0.00000001"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                placeholder="0.01000000"
                required
                className={inputCls}
              />
            </div>
          </div>
          {pendingConfirm && (
            <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/[0.06] p-4 space-y-1.5">
              <p className="text-xs font-semibold text-cyan-300 flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5" /> Confirm Rate Change
              </p>
              <p className="text-xs text-slate-400">
                New rate: <span className="font-mono text-white">{parseFloat(rate || '0').toFixed(8)}</span> UKC per 1{' '}
                <span className="font-mono text-amber-300">{selectedWorld?.currencySymbol ?? 'WLD'}</span> for{' '}
                <span className="text-white font-medium">{selectedWorld?.name ?? worldId}</span>. All future exchanges for this world will use this rate.
              </p>
            </div>
          )}
          <div className="flex gap-3 pt-1">
            {pendingConfirm ? (
              <>
                <button
                  type="button"
                  onClick={() => setPendingConfirm(false)}
                  className="text-xs text-slate-400 px-4 py-2 rounded-xl border border-white/8 hover:border-white/15 transition-all"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={setRateMutation.isPending}
                  className="btn-primary text-xs font-semibold px-5 py-2 rounded-xl flex items-center gap-2"
                >
                  {setRateMutation.isPending
                    ? <><span className="w-3.5 h-3.5 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" /> Setting...</>
                    : <><ArrowLeftRight className="w-3.5 h-3.5" /> Apply Rate</>}
                </button>
              </>
            ) : (
              <button
                type="submit"
                disabled={!worldId || !rate}
                className="btn-primary text-xs font-semibold px-5 py-2 rounded-xl flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ArrowLeftRight className="w-3.5 h-3.5" /> Review Rate
              </button>
            )}
          </div>
          {setRateMutation.isError && (
            <p className="text-red-400 text-xs flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5" />
              {(setRateMutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Failed to set rate'}
            </p>
          )}
        </motion.form>
      )}

      {/* Rate cards */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-4">Current Active Rates</h3>
        {worlds.length === 0 ? (
          <div className="glass-card p-8 text-center">
            <Globe className="w-8 h-8 text-slate-700 mx-auto mb-2" />
            <p className="text-slate-500 text-sm">No worlds registered</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {worlds.map((w: Record<string, string>, i: number) => (
              <ExchangeRateCard key={w['id']} worldId={w['id']} worldName={w['name']} currencySymbol={w['currencySymbol'] ?? 'WLD'} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

